import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { logEvent } from '~/config/firebase.config';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import { brandTokens, isHexColor, safePrimary, safeSecondary } from '~/services/branding/brandingColors';
import {
    BRAND_CORNERS,
    BrandCorner,
    ClientBranding,
    EMPTY_BRANDING,
    logoFromFile,
    subscribeClientBranding,
    writeClientBranding,
} from '~/services/branding/clientBranding';
import { keepSubscription } from '~/services/subscription/subscriptionActions';
import { keepFailure } from '~/utils/keepOffer';
import { unixDate } from '~/utils/pauseFormat';
import type { ProBlock, ProOffer } from '~/utils/proOffer';

import styles from './Account.redesign.module.css';
import own from './BrandingCard.module.css';
import switchStyles from './PlayerLanguageCard.module.css';

/** Délai avant d'enregistrer une couleur : le sélecteur envoie une valeur à chaque mouvement. */
const SAVE_DELAY_MS = 700;

function IconPalette() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.8-.8 1.8-1.8 0-.5-.2-.9-.5-1.2-.3-.3-.5-.8-.5-1.2 0-1 .8-1.8 1.8-1.8H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8z" />
            <circle cx="7.5" cy="11.5" r="1" />
            <circle cx="10.5" cy="7.5" r="1" />
            <circle cx="15" cy="7.5" r="1" />
        </svg>
    );
}

function ColorField({
    label,
    value,
    safe,
    onChange,
    disabled = false,
}: {
    label: string;
    value: string;
    safe: string;
    onChange: (hex: string) => void;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const [text, setText] = useState(value);
    useEffect(() => setText(value), [value]);

    const onText = (e: ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value.trim();
        setText(next);
        const hex = next.startsWith('#') ? next : `#${next}`;
        if (isHexColor(hex)) onChange(hex.toLowerCase());
    };

    return (
        <div className={own.colorField}>
            <span className={styles.label}>{label}</span>
            <div className={own.colorRow}>
                <input
                    type="color"
                    className={own.swatch}
                    value={value}
                    onChange={(e) => onChange(e.target.value.toLowerCase())}
                    aria-label={label}
                    disabled={disabled}
                />
                <input
                    className={`${styles.input} ${own.hex}`}
                    value={text}
                    onChange={onText}
                    maxLength={7}
                    disabled={disabled}
                />
            </div>
            {safe !== value && (
                <span className={own.adjusted}>
                    <span className={own.adjustedDot} style={{ background: safe }} />
                    {t('account.brand.adjusted', { color: safe })}
                </span>
            )}
        </div>
    );
}

/** Aperçu de la TV (accueil et pendant un jeu) avec les couleurs réellement affichées. */
function TvPreview({ branding, venueName }: { branding: ClientBranding; venueName: string }) {
    const { t } = useTranslation();
    const tokens = brandTokens(branding.primary, branding.secondary) as React.CSSProperties;
    return (
        <div className={own.previews} style={tokens}>
            <figure className={own.previewItem}>
                <div className={own.tv}>
                    <div className={own.tvBar}>
                        {branding.logo ? (
                            <img className={own.tvBarLogo} src={branding.logo} alt="" />
                        ) : (
                            <img className={own.tvBarPicto} src="/assets/images/logo-unboared-sm-tr.png" alt="" />
                        )}
                        <span className={own.tvVenue}>{venueName}</span>
                    </div>
                    <div className={own.tvHero}>
                        <span className={own.tvBadge}>{t('account.brand.previewBadge')}</span>
                        <span className={own.tvTitle}>Blind Test</span>
                        <span className={own.tvButton}>▶ {t('account.brand.previewStart')}</span>
                    </div>
                </div>
                <figcaption className={own.caption}>{t('account.brand.previewHome')}</figcaption>
            </figure>
            <figure className={own.previewItem}>
                <div className={`${own.tv} ${own.tvGame}`}>
                    <div className={own.tvGameScene}>
                        <span className={own.tvGameQuestion}>{t('account.brand.previewQuestion')}</span>
                        <div className={own.tvAnswers}>
                            <span />
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                    {branding.logo && (
                        <img className={`${own.tvCorner} ${own[branding.corner]}`} src={branding.logo} alt="" />
                    )}
                </div>
                <figcaption className={own.caption}>{t('account.brand.previewGame')}</figcaption>
            </figure>
        </div>
    );
}

/** Bouton qui ouvre le portail Stripe sur la confirmation du Pro mensuel ou annuel. */
function ProSwitchButton({
    clientID,
    period,
    className,
    children,
}: {
    clientID: string;
    period: 'month' | 'year';
    className: string;
    children: React.ReactNode;
}) {
    const { i18n } = useTranslation();
    return (
        <StripeServerForm
            className={styles.inlineForm}
            route="/create-customer-portal-session"
            clientId={clientID}
            lang={i18n.language}
            intent={period === 'year' ? 'pro_annual' : 'pro'}
        >
            <button
                type="submit"
                className={className}
                onClick={() => logEvent('account_brand_pro_click', 'Account', { clientID, period })}
            >
                {children}
            </button>
        </StripeServerForm>
    );
}

/**
 * Encart « Passer en Pro » : au mois ou à l'année (2 mois offerts). Depuis l'annuel standard,
 * seulement à l'année (pas de retour au mensuel par ce bouton).
 */
function ProChoice({ clientID, offer }: { clientID: string; offer: ProOffer }) {
    const { t } = useTranslation();
    const [period, setPeriod] = useState<'month' | 'year'>('month');
    const yearly = !offer.monthly || (period === 'year' && !offer.yearlyOff);
    let body: string;
    if (offer.from === 'ended')
        body = t(yearly ? 'account.brand.proBodyEndedYearly' : 'account.brand.proBodyEnded', {
            price: yearly ? offer.annual.price : offer.monthly?.pro,
            twelveMonths: offer.annual.twelveMonths,
        });
    else if (offer.monthly && !yearly) body = t('account.brand.proBody', offer.monthly);
    // Annuel standard payé : seule la différence au prorata. En essai, `end_trial` prélève 690 € pleins.
    else if (offer.from === 'annual' && !offer.trialing) body = t('account.brand.proBodyFromAnnual', offer.annual);
    else body = t('account.brand.proBodyYearly', offer.annual);
    let trial: string | null = null;
    if (offer.trialing && offer.monthly && !yearly) trial = t('account.brand.proTrial', offer.monthly);
    else if (offer.trialing) trial = t('account.brand.proTrialYearly', offer.annual);
    return (
        <>
            {offer.monthly && !offer.yearlyOff && (
                <div className={own.periods} role="radiogroup" aria-label={t('account.brand.proPeriod')}>
                    {(['month', 'year'] as const).map((p) => (
                        <button
                            key={p}
                            type="button"
                            role="radio"
                            aria-checked={period === p}
                            className={`${own.cornerBtn} ${period === p ? own.cornerOn : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {t(p === 'month' ? 'account.brand.proMonthly' : 'account.brand.proYearly')}
                        </button>
                    ))}
                </div>
            )}
            <p>{body}</p>
            {trial && <p className={own.small}>{trial}</p>}
            <ProSwitchButton clientID={clientID} period={yearly ? 'year' : 'month'} className={styles.saveBtn}>
                {t('account.brand.proCta')}
            </ProSwitchButton>
        </>
    );
}

/**
 * Pas de bouton « Passer en Pro » tout de suite : le geste qui le débloque, sur place.
 * - arrêt programmé → « Garder Unboared » (rien n'est prélevé au clic), puis le bouton Pro apparaît ;
 * - impayé → mise à jour de la carte (portail Stripe) ;
 * - pause → rubrique Abonnement (« Reprendre ») ;
 * - membre, remise partenaire, cas rares → une phrase (contact seulement quand aucun geste ne suffit).
 * Mesure : `account_brand_pro_blocked` (`reason`) à l'affichage, `account_brand_pro_unblock` au clic.
 */
function ProBlocked({
    clientID,
    reason,
    subscriptionEnd,
    onKeep,
    onOpenSubscription,
}: {
    clientID: string;
    reason: ProBlock;
    subscriptionEnd: number | null;
    onKeep: () => void;
    onOpenSubscription: () => void;
}) {
    const { t, i18n } = useTranslation();
    const [keeping, setKeeping] = useState(false);
    // Clé du message d'échec de « Garder Unboared » (null = pas d'erreur).
    const [keepFailed, setKeepFailed] = useState<string | null>(null);
    useEffect(() => {
        logEvent('account_brand_pro_blocked', 'Account', { clientID, reason });
    }, [clientID, reason]);
    const clicked = () => logEvent('account_brand_pro_unblock', 'Account', { clientID, reason });

    if (reason === 'cancel_scheduled') {
        // Même geste et mêmes mesures que la carte « Garder Unboared » de l'Abonnement (origin `brand`).
        const keep = async () => {
            clicked();
            setKeeping(true);
            setKeepFailed(null);
            logEvent('account_keep_click', 'Account', { clientID, origin: 'brand' });
            try {
                await keepSubscription(clientID, 'account');
                logEvent('account_keep_done', 'Account', { clientID, origin: 'brand' });
                onKeep();
            } catch (error) {
                const failure = keepFailure(error);
                // Autre onglet en train de garder : relire le statut plutôt qu'afficher une erreur.
                if (failure.reread) onKeep();
                else setKeepFailed(failure.messageKey);
            }
            setKeeping(false);
        };
        return (
            <>
                <p>
                    {t('account.brand.proKeepFirst', {
                        date: subscriptionEnd ? unixDate(subscriptionEnd, i18n.language) : '',
                    })}
                </p>
                <button type="button" className={styles.saveBtn} onClick={keep} disabled={keeping}>
                    {keeping ? t('account.keep.keeping') : t('account.keep.cta')}
                </button>
                {keepFailed && (
                    <span className={`${styles.msg} ${styles.msgError}`} role="alert">
                        {t(keepFailed)}
                    </span>
                )}
            </>
        );
    }
    if (reason === 'payment_issue') {
        return (
            <>
                <p>{t('account.brand.proPayFirst')}</p>
                <StripeServerForm
                    className={styles.inlineForm}
                    route="/create-customer-portal-session"
                    clientId={clientID}
                    lang={i18n.language}
                >
                    <button type="submit" className={styles.saveBtn} onClick={clicked}>
                        {t('account.brand.proPayFirstCta')}
                    </button>
                </StripeServerForm>
            </>
        );
    }
    if (reason === 'paused') {
        return (
            <>
                <p>{t('account.brand.proPausedFirst')}</p>
                <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={() => {
                        clicked();
                        onOpenSubscription();
                    }}
                >
                    {t('account.brand.proPausedFirstCta')}
                </button>
            </>
        );
    }
    const text: Record<string, string> = {
        member: 'account.brand.proMember',
        discount: 'account.brand.proDiscount',
        error: 'account.brand.proError',
    };
    return <p>{t(text[reason] || 'account.brand.proContact')}</p>;
}

/**
 * Carte « Ma marque » (page Mon compte, admins des bars) : logo + 2 couleurs + coin du logo
 * pendant les jeux, avec aperçu de la TV. Enregistré tout seul (comme la carte langue).
 * Tout le monde peut préparer sa marque ; elle ne s'AFFICHE qu'en formule Pro (`plan`), sinon
 * l'encart « Passer en Pro » ouvre le portail Stripe sur l'écran de confirmation (`intent=pro`,
 * ou `intent=pro_annual` pour le Pro à l'année) — ou, sans abonnement en cours, la page de
 * paiement d'un nouvel abonnement Pro (même bouton, stripe-b2b décide). Sinon `proBlock` dit quoi
 * faire d'abord (ProBlocked). Un Pro mensuel voit en dessous un lien « à l'année ».
 * Réglage : `clients/{id}/public/branding` (services/branding/clientBranding.ts).
 * Mesures : `account_brand_view`, `account_brand_logo_set`, `account_brand_toggle`,
 * `account_brand_pro_click` (`period` : month / year).
 */
export function BrandingCard({
    clientID,
    venueName,
    isPro,
    proOffer,
    proBlock,
    subscriptionEnd,
    onKeep,
    onOpenSubscription,
    focus,
    index,
}: {
    clientID: string;
    venueName: string;
    isPro: boolean;
    proOffer: ProOffer | null;
    /** Raison de l'absence de bouton Pro (null : statut pas encore lu). */
    proBlock: ProBlock | null;
    subscriptionEnd: number | null;
    /** Abonnement gardé (arrêt programmé retiré) : relire le statut, le bouton Pro apparaît. */
    onKeep: () => void;
    onOpenSubscription: () => void;
    focus: boolean;
    index: number;
}) {
    const { t } = useTranslation();
    // Retour de stripe-b2b quand l'écran Stripe du passage en Pro n'a pas pu s'ouvrir (`pro=unavailable`)
    // ou que le lieu est déjà dans cette formule (`pro=already`) : lu une fois, puis retiré de l'adresse
    // pour ne pas réapparaître au rechargement.
    const [searchParams, setSearchParams] = useSearchParams();
    const [proUnavailable] = useState(() => searchParams.get('pro') === 'unavailable');
    // Abonnement au prix Pro, lieu pas encore « pro » : stripe-b2b le rattrape en quelques secondes
    // (la formule arrive par l'abonnement temps réel au lieu). Passé 20 s, ce n'est plus une
    // question d'attente : phrase de contact (le serveur journalise le cas en 🚨).
    const [syncWaited, setSyncWaited] = useState(false);
    const syncing = !isPro && proOffer?.from === 'pro_monthly';
    useEffect(() => {
        if (!syncing) return undefined;
        const timer = setTimeout(() => setSyncWaited(true), 20_000);
        return () => clearTimeout(timer);
    }, [syncing]);
    useEffect(() => {
        if (!searchParams.has('pro')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('pro');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);
    const ref = useRef<HTMLElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    // undefined = pas encore lu.
    const [branding, setBranding] = useState<ClientBranding | undefined>(undefined);
    const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const [error, setError] = useState<'load' | 'save' | 'logo' | 'logo_too_big' | null>(null);
    const [readingLogo, setReadingLogo] = useState(false);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Modifs locales pas encore envoyées : réappliquées par-dessus chaque relecture, puis
    // envoyées SEULES (fusion) — on ne réécrit jamais un champ qu'on n'a pas touché.
    const pending = useRef<Partial<ClientBranding>>({});
    const current = useRef<ClientBranding>(EMPTY_BRANDING);
    const exists = useRef(false);
    // Lu au moment de l'envoi (différé) : la formule peut changer entre-temps (retour du portail).
    const isProRef = useRef(isPro);
    isProRef.current = isPro;
    const locked = error === 'load';

    const show = (b: ClientBranding) => {
        current.current = b;
        setBranding(b);
    };

    useEffect(() => {
        setBranding(undefined);
        setError(null);
        return subscribeClientBranding(
            clientID,
            (b, docExists) => {
                exists.current = docExists;
                show({ ...b, ...pending.current });
            },
            () => {
                show(EMPTY_BRANDING);
                setError('load');
            }
        );
    }, [clientID]);

    useEffect(() => {
        logEvent('account_brand_view', 'Account', { clientID, pro: isPro });
        if (focus) ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        // Une mesure par affichage de la carte.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => clearTimeout(saveTimer.current ?? undefined), []);

    const flush = () => {
        const patch = pending.current;
        pending.current = {};
        // `enabled` n'est envoyé que s'il a été touché (interrupteur, 1er logo) ; hors Pro
        // jamais vrai (les règles refuseraient tout le reste de l'envoi).
        if ('enabled' in patch) patch.enabled = isProRef.current && patch.enabled === true;
        // Premier enregistrement : document complet (les règles exigent couleurs et coin).
        const { logo, primary, secondary, corner } = current.current;
        const body = exists.current ? patch : { logo, primary, secondary, corner, ...patch };
        setStatus('saving');
        writeClientBranding(clientID, body)
            .then(() => setStatus('saved'))
            .catch(() => {
                // La relecture remet l'écran sur ce qui est vraiment enregistré.
                setStatus('idle');
                setError('save');
            });
    };

    const save = (patch: Partial<ClientBranding>, delay = 0) => {
        if (locked) return;
        pending.current = { ...pending.current, ...patch };
        show({ ...current.current, ...patch });
        setError(null);
        clearTimeout(saveTimer.current ?? undefined);
        saveTimer.current = setTimeout(flush, delay);
    };

    const onLogoFile = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !branding || locked) return;
        setReadingLogo(true);
        setError(null);
        try {
            const logo = await logoFromFile(file);
            const first = !current.current.logo && !current.current.enabled;
            // Premier logo d'un client Pro : affiché tout de suite, c'est ce qu'il vient chercher.
            save(isProRef.current && first ? { logo, enabled: true } : { logo });
            logEvent('account_brand_logo_set', 'Account', { clientID, pro: isPro });
        } catch (err) {
            setError((err as Error)?.message === 'logo_too_big' ? 'logo_too_big' : 'logo');
        } finally {
            setReadingLogo(false);
        }
    };

    if (!branding) {
        return (
            <section ref={ref} className={styles.card} style={{ '--i': index } as React.CSSProperties}>
                <div className={styles.cardHead}>
                    <div className={styles.cardIcon}>
                        <IconPalette />
                    </div>
                    <div className={styles.cardTitle}>{t('account.brand.title')}</div>
                </div>
            </section>
        );
    }

    return (
        <section ref={ref} className={styles.card} style={{ '--i': index } as React.CSSProperties}>
            <div className={styles.cardHead}>
                <div className={styles.cardIcon}>
                    <IconPalette />
                </div>
                <div className={styles.cardTitle}>{t('account.brand.title')}</div>
                <span className={own.proTag}>Pro</span>
                <span className={own.status} aria-live="polite">
                    {status === 'saving' && t('account.brand.saving')}
                    {status === 'saved' && t('account.brand.saved')}
                </span>
            </div>
            <p className={styles.hint}>{t('account.brand.hint')}</p>

            <div className={own.controls}>
                <div className={own.logoField}>
                    <span className={styles.label}>{t('account.brand.logo')}</span>
                    <div className={own.logoRow}>
                        <div className={own.logoBox}>
                            {branding.logo ? (
                                <img src={branding.logo} alt="" />
                            ) : (
                                <span>{t('account.brand.noLogo')}</span>
                            )}
                        </div>
                        <div className={own.logoActions}>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={() => fileRef.current?.click()}
                                disabled={readingLogo || locked}
                            >
                                {branding.logo ? t('account.brand.changeLogo') : t('account.brand.chooseLogo')}
                            </button>
                            {branding.logo && (
                                <button
                                    type="button"
                                    className={own.linkBtn}
                                    onClick={() => save({ logo: null })}
                                    disabled={locked}
                                >
                                    {t('account.brand.removeLogo')}
                                </button>
                            )}
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            // `hidden` ne suffit pas : index.css fait `input { all: unset }`.
                            style={{ display: 'none' }}
                            onChange={onLogoFile}
                        />
                    </div>
                    <span className={own.small}>{t('account.brand.logoHelp')}</span>
                </div>

                <div className={own.colors}>
                    <ColorField
                        label={t('account.brand.primary')}
                        value={branding.primary}
                        safe={safePrimary(branding.primary)}
                        onChange={(primary) => save({ primary }, SAVE_DELAY_MS)}
                        disabled={locked}
                    />
                    <ColorField
                        label={t('account.brand.secondary')}
                        value={branding.secondary}
                        safe={safeSecondary(branding.secondary)}
                        onChange={(secondary) => save({ secondary }, SAVE_DELAY_MS)}
                        disabled={locked}
                    />
                </div>

                <div className={own.cornerField}>
                    <span className={styles.label}>{t('account.brand.corner')}</span>
                    <div className={own.corners} role="radiogroup" aria-label={t('account.brand.corner')}>
                        {BRAND_CORNERS.map((c: BrandCorner) => (
                            <button
                                key={c}
                                type="button"
                                role="radio"
                                aria-checked={branding.corner === c}
                                className={`${own.cornerBtn} ${branding.corner === c ? own.cornerOn : ''}`}
                                onClick={() => save({ corner: c })}
                                disabled={locked}
                            >
                                <span className={`${own.cornerDot} ${own[c]}`} />
                                {t(`account.brand.corners.${c}`)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <TvPreview branding={branding} venueName={venueName} />

            {isPro ? (
                <button
                    type="button"
                    role="switch"
                    aria-checked={branding.enabled}
                    className={switchStyles.row}
                    onClick={() => {
                        save({ enabled: !branding.enabled });
                        logEvent('account_brand_toggle', 'Account', { clientID, enabled: !branding.enabled });
                    }}
                    disabled={locked}
                >
                    <span className={switchStyles.rowLabel}>{t('account.brand.show')}</span>
                    <span
                        className={`${switchStyles.switch} ${branding.enabled ? switchStyles.switchOn : ''}`}
                        aria-hidden="true"
                    >
                        <span className={switchStyles.knob} />
                    </span>
                </button>
            ) : (
                <div className={own.proBox}>
                    <p className={own.proTitle}>{t('account.brand.proTitle')}</p>
                    {proOffer && proOffer.from !== 'pro_monthly' && <ProChoice clientID={clientID} offer={proOffer} />}
                    {/* Abonnement déjà au prix Pro, formule pas encore recopiée sur le lieu (stripe-b2b
                        la rattrape à la lecture du statut) : quelques secondes. */}
                    {proOffer?.from === 'pro_monthly' && (
                        <p>{t(syncWaited ? 'account.brand.proContact' : 'account.brand.proSyncing')}</p>
                    )}
                    {!proOffer && proBlock && (
                        <ProBlocked
                            clientID={clientID}
                            reason={proBlock}
                            subscriptionEnd={subscriptionEnd}
                            onKeep={onKeep}
                            onOpenSubscription={onOpenSubscription}
                        />
                    )}
                </div>
            )}
            {proUnavailable && !isPro && (
                <span className={`${styles.msg} ${styles.msgError}`} role="alert">
                    {t('account.brand.proUnavailable')}
                </span>
            )}
            {isPro && proOffer?.from === 'pro_monthly' && (
                <div className={own.proYearly}>
                    <span>{t('account.brand.proYearlyHint', proOffer.annual)}</span>
                    <ProSwitchButton clientID={clientID} period="year" className={own.linkBtn}>
                        {t('account.brand.proYearlyCta')}
                    </ProSwitchButton>
                </div>
            )}

            {error && (
                <span className={`${styles.msg} ${styles.msgError}`} role="alert">
                    {t(`account.brand.errors.${error}`)}
                </span>
            )}
        </section>
    );
}
